import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { RegisteredPlayer, Team } from '@sportninja/common/types/Registration'
import { uiReducer, initialUIState } from './reducer'
import { UIContextValue, UIActionType, ModalName, HighlightType } from './types'
import { RegistrationTableDivision } from '../../types'

const UIContext = createContext<UIContextValue | undefined>(undefined)

const CLEANUP_INTERVAL_MS = 1000

interface UIProviderProps {
  children: React.ReactNode
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(uiReducer, initialUIState)

  const openModal = useCallback((modal: ModalName) => {
    dispatch({ type: UIActionType.OPEN_MODAL, payload: modal })
  }, [])

  const closeModal = useCallback((modal: ModalName) => {
    dispatch({ type: UIActionType.CLOSE_MODAL, payload: modal })
  }, [])

  const closeAllModals = useCallback(() => {
    dispatch({ type: UIActionType.CLOSE_ALL_MODALS })
  }, [])

  const setSelectedDivisionModal = useCallback(
    (division: RegistrationTableDivision | null) => {
      dispatch({
        type: UIActionType.SET_SELECTED_DIVISION_MODAL,
        payload: division,
      })
    },
    []
  )

  const setSelectedTeam = useCallback((team: Team | null) => {
    dispatch({ type: UIActionType.SET_SELECTED_TEAM, payload: team })
  }, [])

  const setSelectedPlayer = useCallback((player: RegisteredPlayer | null) => {
    dispatch({ type: UIActionType.SET_SELECTED_PLAYER, payload: player })
  }, [])

  const setBucketToDelete = useCallback((bucket: Team | null) => {
    dispatch({ type: UIActionType.SET_BUCKET_TO_DELETE, payload: bucket })
  }, [])

  const setSearchText = useCallback((text: string) => {
    dispatch({ type: UIActionType.SET_SEARCH_TEXT, payload: text })
  }, [])

  const setSelectedStatuses = useCallback((statuses: number[]) => {
    dispatch({ type: UIActionType.SET_SELECTED_STATUSES, payload: statuses })
  }, [])

  const toggleTeamRow = useCallback((teamId: string) => {
    dispatch({ type: UIActionType.TOGGLE_TEAM_ROW, payload: teamId })
  }, [])

  const expandAllTeamRows = useCallback((teamIds: string[]) => {
    dispatch({ type: UIActionType.EXPAND_ALL_TEAM_ROWS, payload: teamIds })
  }, [])

  const collapseAllTeamRows = useCallback(() => {
    dispatch({ type: UIActionType.COLLAPSE_ALL_TEAM_ROWS })
  }, [])

  const toggleDivision = useCallback((divisionId: string) => {
    dispatch({ type: UIActionType.TOGGLE_DIVISION, payload: divisionId })
  }, [])

  const setOpenDivisions = useCallback((divisions: Record<string, boolean>) => {
    dispatch({ type: UIActionType.SET_OPEN_DIVISIONS, payload: divisions })
  }, [])

  // Filter actions
  const setSelectedDivision = useCallback((divisionId: string) => {
    dispatch({ type: UIActionType.SET_SELECTED_DIVISION, payload: divisionId })
  }, [])

  const setOnlyBuckets = useCallback((value: boolean) => {
    dispatch({ type: UIActionType.SET_ONLY_BUCKETS, payload: value })
  }, [])

  const setOnlyDeferredPayments = useCallback((value: boolean) => {
    dispatch({ type: UIActionType.SET_ONLY_DEFERRED_PAYMENTS, payload: value })
  }, [])

  const setAllExpanded = useCallback((value: boolean) => {
    dispatch({ type: UIActionType.SET_ALL_EXPANDED, payload: value })
  }, [])

  // Player selection actions
  const setSelectedPlayerRowKeys = useCallback((keys: React.Key[]) => {
    dispatch({ type: UIActionType.SET_SELECTED_PLAYER_ROW_KEYS, payload: keys })
  }, [])

  const setSelectedPlayers = useCallback((players: RegisteredPlayer[]) => {
    dispatch({ type: UIActionType.SET_SELECTED_PLAYERS, payload: players })
  }, [])

  const clearPlayerSelection = useCallback(() => {
    dispatch({ type: UIActionType.CLEAR_PLAYER_SELECTION })
  }, [])

  // Team selection for moving
  const setSelectedTeamRowKeys = useCallback((keys: React.Key[]) => {
    dispatch({ type: UIActionType.SET_SELECTED_TEAM_ROW_KEYS, payload: keys })
  }, [])

  const setSelectedTeamsForMove = useCallback((teams: Team[]) => {
    dispatch({ type: UIActionType.SET_SELECTED_TEAMS_FOR_MOVE, payload: teams })
  }, [])

  const setMoveTeamsOriginDivision = useCallback(
    (divisionId: string | null) => {
      dispatch({
        type: UIActionType.SET_MOVE_TEAMS_ORIGIN_DIVISION,
        payload: divisionId,
      })
    },
    []
  )

  const clearTeamSelection = useCallback(() => {
    dispatch({ type: UIActionType.CLEAR_TEAM_SELECTION })
  }, [])

  const setRegistrationDetailDefaultTab = useCallback((tab: '0' | '1') => {
    dispatch({
      type: UIActionType.SET_REGISTRATION_DETAIL_DEFAULT_TAB,
      payload: tab,
    })
  }, [])

  const addHighlightedRow = useCallback(
    (id: string, highlightType: HighlightType = 'success') => {
      dispatch({
        type: UIActionType.ADD_HIGHLIGHTED_ROW,
        payload: { id, highlightType, addedAt: Date.now() },
      })
    },
    []
  )

  // Periodic cleanup of expired highlights — single interval instead of per-row setTimeout
  useEffect(() => {
    if (state.highlightedRows.size === 0) {
      return
    }
    const interval = setInterval(() => {
      dispatch({
        type: UIActionType.CLEAR_EXPIRED_HIGHLIGHTS,
        payload: Date.now(),
      })
    }, CLEANUP_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [state.highlightedRows])

  const isModalOpen = useCallback(
    (modal: ModalName) => state.modals[modal],
    [state.modals]
  )

  const isTeamRowExpanded = useCallback(
    (teamId: string) => state.expandedTeamRows.has(teamId),
    [state.expandedTeamRows]
  )

  const isDivisionOpen = useCallback(
    (divisionId: string) => state.openDivisions[divisionId] ?? true,
    [state.openDivisions]
  )

  const value: UIContextValue = useMemo(
    () => ({
      ...state,
      openModal,
      closeModal,
      closeAllModals,
      setSelectedDivisionModal,
      setSelectedTeam,
      setSelectedPlayer,
      setBucketToDelete,
      setSearchText,
      setSelectedStatuses,
      toggleTeamRow,
      expandAllTeamRows,
      collapseAllTeamRows,
      toggleDivision,
      setOpenDivisions,
      isModalOpen,
      isTeamRowExpanded,
      isDivisionOpen,
      // Filter actions
      setSelectedDivision,
      setOnlyBuckets,
      setOnlyDeferredPayments,
      setAllExpanded,
      // Player selection actions
      setSelectedPlayerRowKeys,
      setSelectedPlayers,
      clearPlayerSelection,
      // Team selection for moving
      setSelectedTeamRowKeys,
      setSelectedTeamsForMove,
      setMoveTeamsOriginDivision,
      clearTeamSelection,
      // Registration details modal
      setRegistrationDetailDefaultTab,
      // Row highlighting
      addHighlightedRow,
    }),
    [
      state,
      openModal,
      closeModal,
      closeAllModals,
      setSelectedDivisionModal,
      setSelectedTeam,
      setSelectedPlayer,
      setBucketToDelete,
      setSearchText,
      setSelectedStatuses,
      toggleTeamRow,
      expandAllTeamRows,
      collapseAllTeamRows,
      toggleDivision,
      setOpenDivisions,
      isModalOpen,
      isTeamRowExpanded,
      isDivisionOpen,
      setSelectedDivision,
      setOnlyBuckets,
      setOnlyDeferredPayments,
      setAllExpanded,
      setSelectedPlayerRowKeys,
      setSelectedPlayers,
      clearPlayerSelection,
      setSelectedTeamRowKeys,
      setSelectedTeamsForMove,
      setMoveTeamsOriginDivision,
      clearTeamSelection,
      setRegistrationDetailDefaultTab,
      addHighlightedRow,
    ]
  )

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>
}

export const useUIContext = () => {
  const context = useContext(UIContext)
  if (context === undefined) {
    throw new Error('useUIContext must be used within UIProvider')
  }
  return context
}
